import { Configuration, PublicClientApplication } from '@azure/msal-browser';

export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_AD_CLIENT_ID!,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_AD_TENANT_ID}`,
    redirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest = {
  scopes: ['User.Read'],
};

const EXPRESS_API = process.env.REACT_APP_EXPRESS_API_URL || 'https://famelia-admin-api-web.azurewebsites.net';

// Permission types for admin dashboard tabs
export interface AdminPermissions {
  patient_inquiries: boolean;
  analytics: boolean;
  moderation: boolean;
  user_reports: boolean;
  account_deletions: boolean;
  compliance_review: boolean;
  newsletter: boolean;
  custom_barcodes: boolean;
  admin_access: boolean;
  signups: boolean;
  recipe_manager: boolean;
  challenges: boolean;
  push_notifications: boolean;
  feedback: boolean;
  icd10_coder: boolean;
  education: boolean;
  insurance_cards: boolean;
}

// Role → tab permissions mapping (roles come from Supabase admin_roles table)
const ROLE_PERMISSIONS: Record<string, AdminPermissions> = {
  super_admin: {
    patient_inquiries: true,
    analytics: true,
    moderation: true,
    user_reports: true,
    account_deletions: true,
    compliance_review: true,
    newsletter: true,
    custom_barcodes: true,
    admin_access: true,
    signups: true,
    recipe_manager: true,
    challenges: true,
    push_notifications: true,
    feedback: true,
    icd10_coder: true,
    education: true,
    insurance_cards: true,
  },
  head_dietitian: {
    patient_inquiries: true,
    analytics: true,
    moderation: false,
    user_reports: false,
    account_deletions: false,
    compliance_review: true,
    newsletter: false,
    custom_barcodes: false,
    admin_access: false,
    signups: true,
    recipe_manager: false,
    challenges: true,
    push_notifications: false,
    feedback: true,
    icd10_coder: true,
    education: true,
    insurance_cards: true,
  },
  dietitian: {
    patient_inquiries: true,
    analytics: false,
    moderation: false,
    user_reports: false,
    account_deletions: false,
    compliance_review: true,
    newsletter: false,
    custom_barcodes: false,
    admin_access: false,
    signups: false,
    recipe_manager: false,
    challenges: false,
    push_notifications: false,
    feedback: false,
    icd10_coder: true,
    education: false,
    insurance_cards: true,
  },
  support: {
    patient_inquiries: true,
    analytics: false,
    moderation: false,
    user_reports: true,
    account_deletions: false,
    compliance_review: false,
    newsletter: false,
    custom_barcodes: false,
    admin_access: false,
    signups: false,
    recipe_manager: false,
    challenges: false,
    push_notifications: false,
    feedback: true,
    icd10_coder: false,
    education: false,
    insurance_cards: false,
  },
  marketing: {
    patient_inquiries: false,
    analytics: false,
    moderation: false,
    user_reports: false,
    account_deletions: false,
    compliance_review: false,
    newsletter: true,
    custom_barcodes: false,
    admin_access: false,
    signups: false,
    recipe_manager: false,
    challenges: false,
    push_notifications: false,
    feedback: false,
    icd10_coder: false,
    education: true,
    insurance_cards: false,
  },
};

const NO_PERMISSIONS: AdminPermissions = {
  patient_inquiries: false,
  analytics: false,
  moderation: false,
  user_reports: false,
  account_deletions: false,
  compliance_review: false,
  newsletter: false,
  custom_barcodes: false,
  admin_access: false,
  signups: false,
  recipe_manager: false,
  challenges: false,
  push_notifications: false,
  feedback: false,
  icd10_coder: false,
  education: false,
  insurance_cards: false,
};

/**
 * Normalize Azure AD identity for allow-list lookup.
 * B2B guest users have username like "user_domain.com#EXT#@tenant.onmicrosoft.com"
 * instead of "user@domain.com". Convert that back to the real email.
 */
export function normalizeEmailForLookup(username?: string): string {
  if (!username) return '';
  const lower = username.toLowerCase();
  if (lower.includes('#ext#')) {
    const prefix = lower.split('#ext#')[0].trim();
    const lastUnderscore = prefix.lastIndexOf('_');
    if (lastUnderscore > 0) {
      return prefix.slice(0, lastUnderscore) + '@' + prefix.slice(lastUnderscore + 1);
    }
  }
  return lower;
}

/**
 * Fetch admin role from the Express API.
 * Returns { role, display_name } on success, null if not found/inactive.
 *
 * One hardcoded bootstrap: info@famelia.app always returns super_admin
 * without a DB call so the root admin can never be locked out.
 */
export async function fetchAdminRole(
  email: string,
  authToken: string,
): Promise<{ role: string; display_name: string } | null> {
  const normalized = email.toLowerCase().trim();

  // Bootstrap — always super_admin, no DB call needed
  if (normalized === 'info@famelia.app') {
    return { role: 'super_admin', display_name: 'Famēlia Admin' };
  }

  try {
    const res = await fetch(
      `${EXPRESS_API}/api/admin/role/${encodeURIComponent(normalized)}`,
      { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return { role: data.role, display_name: data.display_name };
  } catch {
    return null;
  }
}

export function roleToPermissions(role: string): AdminPermissions {
  return ROLE_PERMISSIONS[role] ?? NO_PERMISSIONS;
}

export function isAuthorizedAdmin(email?: string): boolean {
  // We can't know synchronously without a DB call — the App.tsx async flow handles this.
  // This is kept for backward compat but returns true for any non-empty email.
  // The fetchAdminRole call in App.tsx is the real gate.
  return !!email;
}

export function getAdminDisplayName(email?: string): string {
  return email ?? 'Admin';
}
